import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Swords } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { playLose } from "@/lib/celebrate";
import { vaultSpin, vaultReveal, vaultRevealMs, vaultDigit, vaultAlarm } from "@/lib/vaultSounds";
import { VaultPad } from "@/components/vault/VaultPad";
import { VaultHistory } from "@/components/vault/VaultHistory";
import { VaultWinOverlay } from "@/components/vault/VaultWinOverlay";
import { DuelOpponent, DuelTimer } from "@/components/vault/DuelOpponent";
import { UserAvatar } from "@/components/UserBits";
import { CheerBar, useCheerFx } from "@/components/vault/DuelCheers";

const wait = (ms) => new Promise((r) => setTimeout(r, ms));
const END_TEXT = { expired: "Das Duell ist abgelaufen – der Einsatz ist zurück.", declined: "Das Duell wurde abgelehnt – der Einsatz ist zurück.", cancelled: "Das Duell wurde zurückgezogen – der Einsatz ist zurück." };

function useDuel(id) {
  const [d, setD] = useState(null);
  const [offset, setOffset] = useState(0);
  const load = () => api.get(`/vault-duel/${id}`).then(({ data }) => { setOffset(new Date(data.server_now) - Date.now()); setD(data); }).catch((e) => (e.response?.status === 404 ? setD({ missing: true }) : toast.error(formatErr(e))));
  useEffect(() => {
    load();
    const iv = setInterval(() => document.visibilityState === "visible" && load(), 1500);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  return [d, load, offset];
}

function useNow(offset) {
  const [n, setN] = useState(Date.now() + offset);
  useEffect(() => { const iv = setInterval(() => setN(Date.now() + offset), 250); return () => clearInterval(iv); }, [offset]);
  return n;
}

function Pending({ d, load }) {
  const act = async (path) => { try { await api.post(`/vault-duel/${d.id}/${path}`); await load(); } catch (e) { toast.error(formatErr(e)); } };
  return (
    <div className="text-center space-y-4 py-6" data-testid="vault-duel-pending">
      <div className="flex justify-center items-center gap-4">
        <UserAvatar userId={d.me.user?.user_id} name={d.me.user?.name} picture={d.me.user?.picture} size="w-16 h-16" text="text-lg" />
        <Swords className="w-8 h-8 text-rose-300 vault-siren" />
        <UserAvatar userId={d.other.user?.user_id} name={d.other.user?.name} picture={d.other.user?.picture} size="w-16 h-16" text="text-lg" />
      </div>
      <p className="font-display text-xl font-black">{d.incoming ? `${d.other.user?.name} fordert dich heraus!` : `Warte auf ${d.other.user?.name}…`}</p>
      <p className="text-sm text-white/60">Einsatz: <b className="text-amber-300">{d.stake} Punkte</b> · Gewinner bekommt {d.stake * 2}</p>
      {d.incoming ? (
        <div className="flex gap-2 justify-center">
          <button onClick={() => act("accept")} data-testid="vault-duel-accept" className="h-12 px-6 rounded-full bg-gradient-to-r from-rose-500 to-amber-500 font-extrabold">Annehmen & los</button>
          <button onClick={() => act("decline")} data-testid="vault-duel-decline" className="h-12 px-5 rounded-full bg-white/10 font-bold">Ablehnen</button>
        </div>
      ) : (
        <>
          <Loader2 className="w-6 h-6 animate-spin mx-auto text-amber-300" />
          <button onClick={() => act("decline")} data-testid="vault-duel-cancel" className="h-10 px-5 rounded-full bg-white/10 text-sm font-bold">Zurückziehen</button>
        </>
      )}
    </div>
  );
}

function Countdown({ sec }) {
  const last = useRef(null);
  useEffect(() => { if (sec !== last.current) { last.current = sec; vaultDigit(String(9 - sec)); } }, [sec]);
  return (
    <div className="py-16 text-center" data-testid="vault-duel-countdown">
      <p className="text-sm font-bold text-white/60 uppercase tracking-widest">Gleich geht's los</p>
      <p key={sec} className="font-display text-8xl font-black text-amber-300 vault-digit-in vault-pot">{sec}</p>
    </div>
  );
}

function SpectatorResult({ d }) {
  const w = d.result === "a" ? d.me : d.result === "b" ? d.other : null;
  return (
    <div className="text-center space-y-2 py-4 vault-pop" data-testid="vault-duel-result">
      <p className="font-display text-3xl font-black text-amber-300">{w ? `${w.user?.name?.split(" ")[0]} gewinnt! 🏆` : "Unentschieden 🤝"}</p>
      <p className="text-sm text-white/50">Der Code war <b className="tracking-[0.3em] text-amber-200">{d.code}</b> · {d.watchers} {d.watchers === 1 ? "Zuschauer hat" : "Zuschauer haben"} angefeuert</p>
      <Link to="/spiele/tresor" data-testid="vault-duel-own" className="inline-flex mt-2 h-11 px-5 rounded-full bg-gradient-to-r from-rose-500 to-amber-500 font-extrabold items-center gap-2"><Swords className="w-4 h-4" /> Selbst ein Duell starten</Link>
    </div>
  );
}

function Result({ d, onRematch }) {
  if (d.spectator) return <SpectatorResult d={d} />;
  const t = d.result === "win" ? ["Gewonnen! 🏆", `+${d.stake * 2} Punkte`] : d.result === "lose" ? ["Verloren", "Knapp daneben – fordere eine Revanche!"] : ["Unentschieden 🤝", "Ihr bekommt beide euren Einsatz zurück."];
  return (
    <div className="text-center space-y-2 py-4 vault-pop" data-testid="vault-duel-result">
      <p className="font-display text-3xl font-black text-amber-300">{t[0]}</p>
      <p className="text-white/80 font-bold">{t[1]}</p>
      <p className="text-sm text-white/50">Der Code war <b className="tracking-[0.3em] text-amber-200">{d.code}</b> · Du: {d.me.tries} {d.me.tries === 1 ? "Versuch" : "Versuche"}{d.me.cracked ? " (geknackt)" : ""} · {d.other.user?.name}: {d.other.tries} {d.other.tries === 1 ? "Versuch" : "Versuche"}{d.other.cracked ? " (geknackt)" : ""}</p>
      <Link to="/spiele/tresor" onClick={onRematch} data-testid="vault-duel-rematch" className="inline-flex mt-2 h-11 px-5 rounded-full bg-gradient-to-r from-rose-500 to-amber-500 font-extrabold items-center gap-2"><Swords className="w-4 h-4" /> Revanche</Link>
    </div>
  );
}

export default function VaultDuel() {
  const { id } = useParams();
  const { user, refreshUser } = useAuth();
  const [d, load, offset] = useDuel(id);
  const now = useNow(offset);
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [shake, setShake] = useState(false);
  const [door, setDoor] = useState({ angle: 0, lit: 0, spinning: false });
  const [fresh, setFresh] = useState(null);
  const [overlay, setOverlay] = useState(false);
  const seen = useRef({ status: null, otherCracked: false });
  useCheerFx(d);

  useEffect(() => {
    if (!d || d.missing) return;
    if (d.status === "done" && seen.current.status === "live") { refreshUser?.(); if (d.result === "win") setOverlay(true); else if (d.result === "lose") playLose(); }
    if (!d.spectator && d.status === "live" && d.other.cracked && !seen.current.otherCracked && !d.me.cracked) { vaultAlarm(); toast(`${d.other.user?.name} hat geknackt – schaffst du es mit weniger Versuchen?`); }
    seen.current = { status: d.status, otherCracked: d.other.cracked };
  }, [d, refreshUser]);

  const submit = async () => {
    setBusy(true);
    try {
      const guess = value;
      setDoor((x) => ({ angle: x.angle + 720 + Math.round(Math.random() * 360), lit: 0, spinning: true }));
      vaultSpin(700);
      const [{ data }] = await Promise.all([api.post(`/vault-duel/${id}/guess`, { guess }), wait(720)]);
      setDoor((x) => ({ ...x, spinning: false, lit: data.gold }));
      setFresh(guess); vaultReveal(data.gold, data.silver);
      if (!data.gold && !data.silver) { setShake(true); setTimeout(() => setShake(false), 500); }
      else setTimeout(() => toast(`${data.gold} Gold · ${data.silver} Silber`), vaultRevealMs(data.gold, data.silver));
      setValue(""); await load();
    } catch (e) { toast.error(formatErr(e)); await load(); }
    finally { setBusy(false); }
  };

  if (!d) return <div className="py-20 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-amber-500" /></div>;
  if (d.missing) return (
    <div className="max-w-md mx-auto py-16 text-center space-y-4" data-testid="vault-duel-missing">
      <p className="font-display text-2xl font-black">Duell nicht gefunden</p>
      <p className="text-sm text-slate-500">Dieses Duell gibt es nicht (mehr).</p>
      <Link to="/spiele/tresor" data-testid="vault-duel-missing-back" className="inline-flex h-11 px-5 items-center rounded-full pink-gradient text-white font-bold">Zum Tresor</Link>
    </div>
  );
  const startIn = d.start_at ? Math.ceil((new Date(d.start_at) - now) / 1000) : 0;
  const left = d.ends_at ? Math.max(0, Math.ceil((new Date(d.ends_at) - now) / 1000)) : 0;
  const finished = d.me.cracked || d.me.tries >= d.max_tries;
  return (
    <div className="max-w-xl mx-auto space-y-4" data-testid="vault-duel-page">
      <Link to="/spiele/tresor" className="inline-flex items-center gap-1 text-sm font-bold text-rose-600" data-testid="vault-duel-back"><ArrowLeft className="w-4 h-4" /> Zum Tresor</Link>
      <div className="rounded-3xl bg-slate-950 text-white p-5 space-y-4 ring-1 ring-rose-400/30">
        <p className="text-[11px] font-extrabold uppercase tracking-[0.2em] text-rose-300 flex items-center gap-1.5"><Swords className="w-4 h-4" /> Tresor-Duell · {d.stake} Punkte Einsatz{d.spectator && <span className="ml-auto text-amber-300" data-testid="vault-duel-spectator">Zuschauer-Modus</span>}</p>
        {(d.status === "live" || d.status === "done") && d.watchers > 0 && <p className="text-xs text-white/50" data-testid="vault-duel-watchers">{d.watchers} {d.watchers === 1 ? "Zuschauer feuert" : "Zuschauer feuern"} an · {d.cheer_counts.a + d.cheer_counts.b} Emojis</p>}
        {d.status === "pending" && <Pending d={d} load={load} />}
        {END_TEXT[d.status] && <p className="py-8 text-center font-bold text-white/70" data-testid="vault-duel-ended">{END_TEXT[d.status]}</p>}
        {(d.status === "live" || d.status === "done") && (
          <>
            <div className="space-y-2"><DuelOpponent side={d.me} maxTries={d.max_tries} label={d.spectator ? "a" : "me"} /><DuelOpponent side={d.other} maxTries={d.max_tries} label={d.spectator ? "b" : "other"} /></div>
            {d.status === "live" && startIn > 0 && <Countdown sec={startIn} />}
            {d.status === "live" && startIn <= 0 && (
              <>
                <DuelTimer left={left} total={d.seconds} />
                {d.spectator ? <CheerBar d={d} reload={load} /> : finished
                  ? <p className="py-6 text-center font-bold text-amber-200" data-testid="vault-duel-waiting">{d.me.cracked ? `Geknackt in ${d.me.tries} Versuchen! Warte auf ${d.other.user?.name}…` : "Keine Versuche mehr – warte aufs Ergebnis…"}</p>
                  : <VaultPad value={value} setValue={setValue} onSubmit={submit} busy={busy} disabled={false} shake={shake} door={{ ...door, size: 130 }} />}
              </>
            )}
            {d.status === "done" && <Result d={d} />}
            {!d.spectator && <VaultHistory mine={d.mine} fresh={fresh} />}
          </>
        )}
      </div>
      {overlay && <VaultWinOverlay win={{ pot: d.stake * 2, code: d.code }} onShare={null} onClose={() => setOverlay(false)} note={`Du hast das Duell gegen ${d.other.user?.name} gewonnen`} />}
    </div>
  );
}
